<script setup>
// Uma avaliação aberta: a nota, os critérios, o feedback do analista e a
// conversa com os trechos citados em destaque. Na Auditoria, o gestor ajusta a
// nota (com motivo) ou pede uma nova análise; em Minhas avaliações, só leitura.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import { duracao, nota, quando } from './api';
import messages from './i18n';

const props = defineProps({
  avaliacao: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
  podeAjustar: { type: Boolean, default: false },
  salvando: { type: Boolean, default: false },
  erro: { type: String, default: '' },
});

const emit = defineEmits(['ajustar', 'refazer']);

const { t } = useI18n({ messages });

const ajustando = ref(false);
const novaNota = ref('');
const motivo = ref('');
watch(
  () => props.avaliacao.id,
  () => {
    ajustando.value = false;
  }
);
watch(
  () => props.avaliacao.ajuste,
  () => {
    ajustando.value = false;
  }
);

const citacoes = computed(() =>
  Object.fromEntries(
    (props.avaliacao.citacoes || []).map(citacao => [
      citacao.message_id,
      citacao,
    ])
  )
);
const criterios = computed(() =>
  Object.entries(props.avaliacao.criterios_nomes || {})
    .filter(([chave]) => props.avaliacao.criterios?.[chave] !== undefined)
    .map(([chave, nome]) => ({ chave, nome, valor: props.avaliacao.criterios[chave] }))
);
const linkDaConversa = computed(() =>
  frontendURL(
    `accounts/${props.accountId}/conversations/${props.avaliacao.conversa}`
  )
);
const baixa = valor => valor !== null && valor !== undefined && valor < 6;

const abreAjuste = () => {
  novaNota.value = String(props.avaliacao.nota);
  motivo.value = '';
  ajustando.value = true;
};
const salvaAjuste = () =>
  emit('ajustar', { nota: novaNota.value.replace(',', '.'), motivo: motivo.value });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-4">
      <span
        class="text-4xl font-medium leading-none tabular-nums"
        :class="baixa(avaliacao.nota) ? 'text-n-ruby-11' : 'text-n-slate-12'"
      >
        {{ nota(avaliacao.nota) }}
      </span>
      <div class="flex flex-col min-w-0 gap-0.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('NEUXS_GESTAO.DETALHE.ATENDENDO', {
              atendente: avaliacao.atendente,
              cliente: avaliacao.cliente || '—',
            })
          }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('NEUXS_GESTAO.DETALHE.CONVERSA', { id: avaliacao.conversa }) }}
          · {{ quando(avaliacao.avaliada_em) }}
          <template v-if="avaliacao.assunto">
            · {{ avaliacao.assunto }}
          </template>
        </span>
      </div>
      <div class="flex flex-wrap gap-2 ms-auto">
        <a :href="linkDaConversa" target="_blank" rel="noopener">
          <Button
            :label="t('NEUXS_GESTAO.DETALHE.ABRIR')"
            size="sm"
            variant="faded"
            color="slate"
          />
        </a>
        <Button
          v-if="podeAjustar && !ajustando"
          :label="t('NEUXS_GESTAO.DETALHE.AJUSTAR')"
          size="sm"
          variant="faded"
          @click="abreAjuste"
        />
      </div>
    </div>

    <div class="flex flex-wrap gap-2 text-xs">
      <span
        v-for="alerta in avaliacao.alertas"
        :key="alerta"
        class="px-2 py-0.5 rounded-md bg-n-ruby-3 text-n-ruby-11"
      >
        ⚠ {{ t(`NEUXS_GESTAO.ALERTAS.${alerta}`) }}
      </span>
      <span
        v-if="t(`NEUXS_GESTAO.DETALHE.RESULTADOS.${avaliacao.analise.resultado}`)"
        class="px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-12"
      >
        {{ t(`NEUXS_GESTAO.DETALHE.RESULTADOS.${avaliacao.analise.resultado}`) }}
      </span>
      <span class="px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11">
        {{
          t('NEUXS_GESTAO.DETALHE.PRIMEIRA', {
            t: duracao(avaliacao.analise.tempos?.primeira_resposta),
          })
        }}
      </span>
      <span class="px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11">
        {{
          t('NEUXS_GESTAO.DETALHE.ESPERA', {
            t: duracao(avaliacao.analise.tempos?.maior_espera),
          })
        }}
      </span>
    </div>

    <p
      v-if="avaliacao.ajuste"
      class="p-3 m-0 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
    >
      {{
        t('NEUXS_GESTAO.DETALHE.AJUSTE', {
          por: avaliacao.ajuste.por,
          motivo: avaliacao.ajuste.motivo,
        })
      }}
      · {{ t('NEUXS_GESTAO.DETALHE.NOTA_DA_IA', { n: nota(avaliacao.nota_original) }) }}
    </p>

    <form
      v-if="ajustando"
      class="flex flex-wrap items-end gap-3 p-3 rounded-lg border border-n-weak"
      @submit.prevent="salvaAjuste"
    >
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('NEUXS_GESTAO.DETALHE.NOVA_NOTA') }}
        <input v-model="novaNota" type="text" inputmode="decimal" class="!mb-0 w-24" />
      </label>
      <label class="flex flex-col flex-1 gap-1 text-xs text-n-slate-11 min-w-48">
        {{ t('NEUXS_GESTAO.DETALHE.MOTIVO') }}
        <input v-model="motivo" type="text" maxlength="500" class="!mb-0" />
      </label>
      <Button
        type="submit"
        size="sm"
        :label="t('NEUXS_GESTAO.DETALHE.SALVAR')"
        :is-loading="salvando"
      />
      <Button
        v-if="avaliacao.ajuste"
        size="sm"
        variant="faded"
        color="slate"
        :label="t('NEUXS_GESTAO.DETALHE.DESFAZER')"
        @click="emit('ajustar', { nota: '' })"
      />
      <Button
        size="sm"
        variant="ghost"
        color="slate"
        :label="t('NEUXS_GESTAO.DETALHE.CANCELAR')"
        @click="ajustando = false"
      />
      <p v-if="erro" class="w-full m-0 text-xs text-n-ruby-11">{{ erro }}</p>
    </form>

    <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <div
        v-for="criterio in criterios"
        :key="criterio.chave"
        class="flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg bg-n-alpha-2"
      >
        <span class="truncate text-n-slate-11">{{ criterio.nome }}</span>
        <span
          class="font-medium tabular-nums"
          :class="baixa(criterio.valor) ? 'text-n-ruby-11' : 'text-n-slate-12'"
        >
          {{ nota(criterio.valor) }}
        </span>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
      <div class="pt-2 border-t-2 border-n-teal-9">
        <p class="m-0 text-xs uppercase tracking-wide text-n-slate-10">
          {{ t('NEUXS_GESTAO.DETALHE.FORTES') }}
        </p>
        <ul class="m-0 mt-1 ps-4 text-sm text-n-slate-12">
          <li v-for="(item, i) in avaliacao.pontos_fortes" :key="i">{{ item }}</li>
        </ul>
      </div>
      <div class="pt-2 border-t-2 border-n-amber-9">
        <p class="m-0 text-xs uppercase tracking-wide text-n-slate-10">
          {{ t('NEUXS_GESTAO.DETALHE.MELHORAR') }}
        </p>
        <ul class="m-0 mt-1 ps-4 text-sm text-n-slate-12">
          <li v-for="(item, i) in avaliacao.melhorar" :key="i">{{ item }}</li>
        </ul>
      </div>
      <div class="pt-2 border-t-2 border-n-blue-9">
        <p class="m-0 text-xs uppercase tracking-wide text-n-slate-10">
          {{ t('NEUXS_GESTAO.DETALHE.SUGESTAO') }}
        </p>
        <p class="m-0 mt-1 text-sm text-n-slate-12">{{ avaliacao.sugestao }}</p>
      </div>
    </div>

    <div>
      <p class="m-0 mb-2 text-xs uppercase tracking-wide text-n-slate-10">
        {{ t('NEUXS_GESTAO.DETALHE.TRECHOS') }}
      </p>
      <div
        class="flex flex-col gap-2 p-3 overflow-y-auto rounded-lg max-h-[28rem] bg-n-alpha-1"
      >
        <div
          v-for="mensagem in avaliacao.mensagens"
          :key="mensagem.id"
          class="max-w-[85%] px-3 py-2 text-sm rounded-xl"
          :class="[
            mensagem.autor === 'cliente'
              ? 'self-start bg-n-alpha-2'
              : 'self-end bg-n-blue-3',
            citacoes[mensagem.id]?.tipo === 'ruim'
              ? 'ring-1 ring-n-amber-9'
              : '',
            citacoes[mensagem.id]?.tipo === 'boa' ? 'ring-1 ring-n-teal-9' : '',
          ]"
        >
          <span class="block text-xs text-n-slate-10">
            {{
              mensagem.nome ||
              t(`NEUXS_GESTAO.DETALHE.AUTORES.${mensagem.autor}`)
            }}
            · {{ quando(mensagem.em) }}
          </span>
          <span class="text-n-slate-12 break-words whitespace-pre-wrap">{{
            mensagem.texto
          }}</span>
          <span
            v-if="citacoes[mensagem.id]"
            class="block mt-1 text-xs"
            :class="
              citacoes[mensagem.id].tipo === 'ruim'
                ? 'text-n-amber-11'
                : 'text-n-teal-11'
            "
          >
            {{ citacoes[mensagem.id].tipo === 'ruim' ? '⚠' : '✓' }}
            {{ citacoes[mensagem.id].comentario }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="podeAjustar" class="flex justify-end">
      <Button
        :label="t('NEUXS_GESTAO.DETALHE.REFAZER')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-refresh-cw"
        @click="emit('refazer')"
      />
    </div>
  </div>
</template>
